import { CommentState } from '@src/types/checks'
import { OwlCategoryNames, OwlRuleNames, OwlUrlNames, UndoReplacementName } from './types'

export const GRAMMAR = 'grammar'
export const SPELLCHECK = 'spellcheck'
export const LANGUAGE = 'language'
export const LANGUAGE_WITH_REPLACEMENTS = 'languageWithReplacements'
export const PLAGIARISM = 'plagiarism'
export const ACCEPT = 'accepted'
export const REJECT = 'rejected'
export const READ = 'read'
export const NEW = 'new'
export const HIDDEN = 'hidden'
export const GATED = 'gated' as const
export const CUSTOM = 'custom'
export const BLURRED_CARD_KEY = 'blurred'
export const PARAPHRASE = 'paraphraser'
export const UNSUPPORTED_CLAIMS = 'unsupported_claims'
export const UNDO = 'undo'
export const TRUNCATE_LENGTH = 22
export const REPLACEMENT_VALUE_THRESHOLD_LENGTH = 25
export const OWL_LINK_SOURCE: string = 'owl learn it link out'

export type SPELLCHECK = typeof SPELLCHECK
export type LANGUAGE = typeof LANGUAGE
export type LANGUAGE_WITH_REPLACEMENTS = typeof LANGUAGE_WITH_REPLACEMENTS
export type PLAGIARISM = typeof PLAGIARISM
export type GRAMMAR = typeof GRAMMAR
export type CUSTOM = typeof CUSTOM

export type CardType = SPELLCHECK | LANGUAGE | LANGUAGE_WITH_REPLACEMENTS | PLAGIARISM

export type UserInteractionStates = typeof ACCEPT | typeof REJECT
export type AllInteractionStates =
  | UserInteractionStates
  | typeof READ
  | typeof NEW
  | typeof HIDDEN
  | typeof GATED

export const CARD_STATES = {
  ACCEPTED: ACCEPT,
  REJECTED: REJECT,
  NEW,
  READ,
  HIDDEN,
  GATED,
}
export type CardStates = CommentState

export type CardText = {
  [K in CardType]: {
    status: { [I in UserInteractionStates]: string }
    cta: { [I in UserInteractionStates]: string }
  }
}

export const cardText: CardText = {
  [SPELLCHECK]: {
    status: {
      [ACCEPT]: 'Accepted',
      [REJECT]: 'Dismissed',
    },
    cta: {
      [ACCEPT]: 'Accept change',
      [REJECT]: 'Dismiss',
    },
  },
  [LANGUAGE]: {
    status: {
      [ACCEPT]: 'I fixed it',
      [REJECT]: 'Dismiss',
    },
    cta: {
      [ACCEPT]: 'Fix in paper',
      [REJECT]: 'Dismiss',
    },
  },
  [LANGUAGE_WITH_REPLACEMENTS]: {
    status: {
      [ACCEPT]: 'Accepted',
      [REJECT]: 'Dismissed',
    },
    cta: {
      [ACCEPT]: 'Accept change',
      [REJECT]: 'Dismiss',
    },
  },
  [PLAGIARISM]: {
    status: {
      [ACCEPT]: 'Dismissed',
      [REJECT]: 'Dismissed',
    },
    cta: {
      [ACCEPT]: 'See sources',
      [REJECT]: 'Dismiss',
    },
  },
}

export const OwlRuleName: OwlRuleNames = {
  replacement: 'replacement',
  deletion: 'deletion',
  replacement_with_three_alt_replacements: 'replacement_with_three_alt_replacements',
  replacement_with_alt_replacement: 'replacement_with_alt_replacement',
  replacement_with_two_alt_replacements_or_deletion:
    'replacement_with_two_alt_replacements_or_deletion',
  replacement_with_two_alt_replacements: 'replacement_with_two_alt_replacements',
  replacement_with_alt_replacement_or_deletion:
    'replacement_with_alt_replacement_or_deletion',
  his_own: 'his_own',
  numerous_different: 'numerous_different',
  and_also: 'and_also',
  negate_meaning: 'negate_meaning',
  in_terms_of: 'in_terms_of',
  time_period_of: 'time_period_of',
  my_personal: 'my_personal',
  spellcheck: 'spellcheck',
  missing_comma_w_intro_element: 'missing_comma_w_intro_element',
  verb_form_lf: 'verb_form_lf',
  he_verb_agr: 'he_verb_agr',
  have_part_agreement: 'have_part_agreement',
  been_part_agreement: 'been_part_agreement',
  sent_frag_lf: 'sent_frag_lf',
  vague_pronoun_unclear_referent_lf_optional:
    'vague_pronoun_unclear_referent_lf_optional',
  comma_before_and: 'comma_before_and',
  missing_comma_clf: 'missing_comma_clf',
  plurals_plural_v2_lf: 'plurals_plural_v2_lf',
  plurals_plural_lf: 'plurals_plural_lf',
  plurals_singular_lf: 'plurals_singular_lf',
  plurals_lf: 'plurals_lf',
  a_plural: 'a_plural',
  unnecessary_capitalization: 'unnecessary_capitalization',
  missing_capitalization: 'missing_capitalization',
  serial_comma_on: 'serial_comma_on',
  unnecessary_comma: 'unnecessary_comma',
}

export const OwlCategoryName: OwlCategoryNames = {
  concision: 'concision',
  spelling: 'spelling',
  missing_introductory_comma: 'missing introductory comma',
  verb_form: 'verb form',
  sentence_fragment: 'sentence fragment',
  vague_reference: 'vague reference',
  comma_between_clauses: 'comma between clauses',
  missing_comma: 'missing comma',
  singular_plural: 'singular/plural',
  capitalization: 'capitalization',
  serial_comma: 'serial comma',
  commas: 'commas',
}

export const OwlUrlCardUtm: string = '?utm_source=cards&utm_medium=learnit'

export const OwlUrl: OwlUrlNames = {
  concision:
    'https://owl.purdue.edu/owl/general_writing/academic_writing/conciseness/index.html',
  spelling:
    'https://owl.purdue.edu/owl/general_writing/grammar/spelling_common_words_that_sound_alike.html',
  commas_after_intro:
    'https://owl.purdue.edu/owl/general_writing/punctuation/commas/commas_after_introductions.html',
  verb: 'https://owl.purdue.edu/owl/general_writing/grammar/verb_tenses/index.html',
  sentence_fragment:
    'https://owl.purdue.edu/owl/general_writing/mechanics/sentence_fragments.html',
  pronoun: 'https://owl.purdue.edu/owl/general_writing/grammar/pronouns/index.html',
  commas:
    'https://owl.purdue.edu/owl/general_writing/punctuation/commas/extended_rules_for_commas.html',
  plurals:
    'https://owl.purdue.edu/owl/general_writing/grammar/count_and_noncount_nouns/plurals_articles_and_quantity_words.html',
  capitalization:
    'https://owl.purdue.edu/owl/general_writing/mechanics/help_with_capitals.html',
}

export const UndoReplacementText: UndoReplacementName = {
  [ACCEPT]: 'Changed!',
  [REJECT]: 'Dismissed',
}



// WEBPACK FOOTER //
// ./src/components/Cards/constants.ts